"use client";

import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { configureThirdPartyScript } from "@/lib/analytics/third-party-scripts";

type CuoralWidgetApi = {
  hideFab?: () => void;
};

function hideCuoralFab() {
  try {
    (
      window as Window & { CuoralWidget?: CuoralWidgetApi }
    ).CuoralWidget?.hideFab?.();
  } catch {
    // ignore — Help menu still owns open path
  }
}

export default function CuoralWidget() {
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    // Only load Cuoral on client side
    if (typeof window === "undefined") return;

    // Defer Cuoral loading to avoid blocking initial page load
    const loadCuoral = () => {
      // Check if Cuoral script is already loaded
      if (document.querySelector("script[data-cuoral-key]")) {
        console.log("[Cuoral] Widget script already loaded");
        hideCuoralFab();
        return;
      }

      // Extract user name parts for first_name and last_name
      let firstName = "";
      let lastName = "";
      if (user?.name) {
        const nameParts = user.name.trim().split(/\s+/);
        firstName = nameParts[0] || "";
        lastName = nameParts.slice(1).join(" ") || "";
      }

      console.log("[Cuoral] Loading widget with config:", {
        publicKey: "080db4a9-2dbd-4f8c-a31e-9306a9937eea",
        hasUser: isAuthenticated && !!user,
        userEmail: user?.email,
        firstName,
        lastName,
      });

      // Create the script element using official Cuoral integration method
      const script = document.createElement("script");
      configureThirdPartyScript(script, "cuoral");

      // Set the source URL and defer loading
      script.src = "https://js.cuoral.com/inline.js";
      // script.defer = true;

      // Move widget up 50px from the bottom (data-margin-bottom read by Cuoral script)
      script.dataset.marginBottom = "80px";

      // D1 / Phase 2.1: Help FAB owns entry — hide Cuoral's default launcher.
      script.dataset.hideFab = "true";

      // Set the public key
      script.dataset.cuoralKey = "080db4a9-2dbd-4f8c-a31e-9306a9937eea";

      // Pass User Details (for personalized support)
      if (isAuthenticated && user?.email) {
        script.dataset.email = user.email; // Recommended: Unique identifier
        if (firstName) {
          script.dataset.firstName = firstName;
        }
        if (lastName) {
          script.dataset.lastName = lastName;
        }
      }

      // Add error handling for debugging
      script.onerror = (error) => {
        console.error("[Cuoral] Failed to load widget script:", error);
        console.warn("[Cuoral] Script URL:", script.src);
        console.warn("[Cuoral] This might indicate:");
        console.warn(" 1. The widget URL is incorrect");
        console.warn(" 2. CORS policy is blocking the request");
        console.warn(" 3. The domain does not exist");
        console.warn(" 4. Network connectivity issues");
      };

      script.onload = () => {
        console.log("[Cuoral] Widget script loaded successfully");
        // Belt-and-suspenders if data-hide-fab is ignored on late init.
        hideCuoralFab();
      };

      // Attach the script to the document head (as per Cuoral's official integration)
      document.head.appendChild(script);
    };

    // Load after page is interactive or after a delay
    let timeoutId: ReturnType<typeof setTimeout> | number | null = null;
    let idleCallbackId: ReturnType<typeof requestIdleCallback> | null = null;

    const scheduleLoad = () => {
      if (typeof window !== "undefined" && "requestIdleCallback" in window) {
        idleCallbackId = requestIdleCallback(() => loadCuoral(), {
          timeout: 3000,
        });
      } else {
        timeoutId = setTimeout(() => loadCuoral(), 2000);
      }
    };

    if (document.readyState === "complete") {
      scheduleLoad();
    } else {
      const loadHandler = () => {
        scheduleLoad();
      };
      window.addEventListener("load", loadHandler);

      return () => {
        window.removeEventListener("load", loadHandler);
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (idleCallbackId) {
          cancelIdleCallback(idleCallbackId);
        }
      };
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (idleCallbackId) {
        cancelIdleCallback(idleCallbackId);
      }
    };
  }, [user, isAuthenticated]);

  return null;
}
